import { useEffect, useState } from 'react';
import { ContactPage, ManageAccounts, Cancel } from '@mui/icons-material';

// material-ui
import {
    Box,
    Button,
    Grid,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { deepPurple } from '@mui/material/colors';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { gridSpacing } from 'app/constant';

import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentToken } from 'features/auth/authSlice';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { axiosInstance } from 'utils/axios';
import { toast } from 'react-toastify';
import { useCallback } from 'react';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const columns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'clientName', label: 'Client Name', minWidth: 120 },
    { id: 'packageName', label: 'Package Name', minWidth: 120 },
    {
        id: 'lockDate',
        label: 'Lock Date',
        type: 'date',
        minWidth: 170
    }
];

const SalesLockRecord = () => {
    const user = useSelector(selectCurrentUser);
    const token = useSelector(selectCurrentToken);
    const theme = useTheme();

    const [sales, setSales] = useState([]);
    const [filterSalesFrom, setFilerSalesFrom] = useState(dayjs().subtract(1, 'days'));
    const [filterSalesTo, setFilerSalesTo] = useState(dayjs());
    const [filterSalesLockRecordClientName, setFilterSalesLockRecordClientName] = useState('');
    const [filterSalesLockRecordWalletTypeId, setFilterSalesLockRecordWalletTypeId] = useState(0);
    const [filterSalesLockRecordSearchUserId, setFilterSalesLockRecordSearchUserId] = useState(0);
    const [customerNameOptions, setCustomerNameOptions] = useState([]);

    const [isLoading, setLoading] = useState(true);
    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);

    const warningAlert = (message) => {
        toast.warning(message, {
            position: toast.POSITION.TOP_CENTER
        });
    };

    const successAlert = (message) => {
        toast.success(message, {
            position: toast.POSITION.TOP_CENTER
        });
    };

    const errorAlert = (message) => {
        toast.error(message, {
            position: toast.POSITION.TOP_CENTER
        });
    };

    const [customerWalletTypes, setCustomerWalletTypes] = useState([]);
    const fetchCustomerWalletTypes = async () => {
        const customerGroups = await axiosInstance.get('/WalletTypes', {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        setCustomerWalletTypes(customerGroups.data);
    };

    //fetch sales start
    const [fetchSalesLockRecordLoading, setFetchSalesLockRecordLoading] = useState(false);
    const fetchSalesLockRecord = async () => {
        if (!filterSalesFrom.isValid() || !filterSalesTo.isValid()) {
            warningAlert('Please fill all required fields.');
            return;
        }
        let salesLockRecordURL = `/Sales/LockDate/All?createdDateFrom=${filterSalesFrom.format(
            'YYYY-MM-DD'
        )}&createdDateTo=${filterSalesTo.format('YYYY-MM-DD')}`;
        if (filterSalesLockRecordClientName !== 0) {
            salesLockRecordURL += `&searchCustomerName=${filterSalesLockRecordClientName}`;
        }
        if (filterSalesLockRecordWalletTypeId !== 0) {
            salesLockRecordURL += `&walletTypeID=${filterSalesLockRecordWalletTypeId}`;
        }
        if (filterSalesLockRecordSearchUserId !== 0) {
            salesLockRecordURL += `&searchUserID=${filterSalesLockRecordSearchUserId}`;
        }

        setFetchSalesLockRecordLoading(true);
        setFilterBtnDisabled(true);
        try {
            const res = await axiosInstance.get(salesLockRecordURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                console.log('Response lock data', res.data);
                setSales(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setFilterBtnDisabled(false);
            setFetchSalesLockRecordLoading(false);
        }
    };

    //fetch customer names for autocomplete start
    const fetchCustomerNamesOptions = async () => {
        try {
            const res = await axiosInstance.get('/Customer/Names', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (res.status === 200) {
                setCustomerNameOptions(res.data);
            }
        } catch (error) {
            console.log(error);
        }
    };
    //fetch customer names for autocomplete end

    //fetch sales end

    // With Promise All
    useEffect(() => {
        setLoading(true); // Set loading to true before starting the fetches

        // Define an array of async functions to fetch data
        const fetchFunctions = [fetchSalesLockRecord(), fetchCustomerNamesOptions()];

        // Use Promise.all to wait for all fetches to complete
        Promise.all(fetchFunctions)
            .then(() => {
                setLoading(false); // Set loading to false after all fetches are done
            })
            .catch((error) => {
                // Handle any errors that occurred during fetching
                console.error('Error fetching data:', error);
                setLoading(false); // Set loading to false even if there was an error
            });
    }, []);

    //sales table start
    function StickyHeadTable() {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(100);

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
        };

        return (
            <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: '50px' }}>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                                        {column.label}
                                    </TableCell>
                                ))}
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sales.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                                        {columns.map((column) => {
                                            let value = row[column.id];
                                            if (column.type && column.type === 'date') {
                                                if (value) {
                                                    const timeString12hr = new Date(
                                                        '1970-01-01T' + value.split('T')[1] + 'Z'
                                                    ).toLocaleTimeString('en-US', {
                                                        timeZone: 'UTC',
                                                        hour12: true,
                                                        hour: 'numeric',
                                                        minute: 'numeric',
                                                        second: 'numeric'
                                                    });
                                                    value = `${value.split('T')[0]} ${timeString12hr}`;
                                                }
                                            }
                                            return (
                                                <TableCell key={column.id} align={column.align}>
                                                    {column.format && typeof value === 'number' ? column.format(value) : value}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={sales.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        );
    }
    //sales table end

    return (
        <Grid container item xs={12} padding="20px">
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>Lock Date From :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={filterSalesFrom}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setFilerSalesFrom(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>Lock Date To :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={filterSalesTo}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setFilerSalesTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Client Name :
                        </Typography>

                        <Autocomplete
                            freeSolo
                            value={filterSalesLockRecordClientName}
                            onChange={(event, newValue) => {
                                setFilterSalesLockRecordClientName(newValue);
                            }}
                            inputValue={filterSalesLockRecordClientName}
                            onInputChange={(event, newInputValue) => {
                                setFilterSalesLockRecordClientName(newInputValue);
                            }}
                            id="controllable-states-demo"
                            options={customerNameOptions}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>
                <Grid item container xs={12} md={12} lg={12}>
                    <Button
                        variant="contained"
                        sx={{
                            float: 'right',
                            display: 'block',
                            mx: 'auto',
                            width: '150px',
                            backgroundColor: deepPurple[500],
                            marginTop: '20px',
                            marginBottom: '30px'
                        }}
                        onClick={() => fetchSalesLockRecord()}
                        disabled={filterBtnDisabled}
                    >
                        Filter
                    </Button>
                </Grid>
                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {fetchSalesLockRecordLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default SalesLockRecord;
